import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useRef, useState } from 'react';

import { Toast } from '#components/toast/toast.js';
import {
	useAiExtractionContextAllValues,
	useAiExtractionContextLastValue,
} from '#screens/ai-workbench/ai-extraction-context.js';
import { useAddToast } from '#state/stores/toast-store.js';

export interface DirectiveApplyBarProps {
	/** Parsed directive payload forwarded to the context's append or replace callback. */
	data: object;
	/**
	 * `configId` of the extraction context this directive's rows belong to. Required whenever more
	 * than one context can be registered on a screen, since the rows are only meaningful to the
	 * context that produced their shape. When omitted, the bar falls back to the last-registered
	 * context. When supplied but not registered, the bar renders nothing rather than applying data
	 * through another context.
	 */
	configId?: string;
}

/**
 * Action bar rendered below markdown directive tables when an AI extraction context is active.
 *
 * Shows "Add to data" and/or "Replace data" buttons when the resolved context provides the
 * corresponding callbacks. Renders nothing when no context is present or neither callback is
 * defined. When the context sets `applyDisabledReason`, the buttons render disabled with that
 * reason as their tooltip.
 *
 */
export function DirectiveApplyBar({ data, configId }: DirectiveApplyBarProps) {
	const contexts = useAiExtractionContextAllValues('value');
	const lastContext = useAiExtractionContextLastValue('value');
	const context = configId === undefined ? lastContext : contexts.find((candidate) => candidate.configId === configId);
	const activeApplyRef = useRef<object | null>(null);
	const currentContextRef = useRef(context);
	const mountedRef = useRef(false);
	const [pendingApply, setPendingApply] = useState<{ context: object; request: object } | null>(null);
	const [appliedResult, setAppliedResult] = useState<{ context: object; data: object } | null>(null);
	const addToast = useAddToast();

	// Applying completes asynchronously. This lifecycle guard invalidates the active request before
	// a retired workbench surface can publish a toast or schedule state after unmounting or changing context.
	useEffect(() => {
		mountedRef.current = true;
		currentContextRef.current = context;
		activeApplyRef.current = null;
		return () => {
			mountedRef.current = false;
			activeApplyRef.current = null;
		};
	}, [context]);

	if (!context?.appendContents && !context?.replaceContents) {
		return null;
	}

	const appendContents = context.appendContents;
	const replaceContents = context.replaceContents;
	const disabledReason = context.applyDisabledReason;
	const saving = pendingApply?.context === context;
	const hasAppliedCurrentData = appliedResult?.context === context && appliedResult.data === data;
	const appliedReason = 'This proposal has been applied. Edit the table to apply the revised proposal.';

	function applyWith(action: 'append' | 'replace', callback: (data: object) => void | Promise<void>) {
		if (!context || activeApplyRef.current !== null || hasAppliedCurrentData) {
			return;
		}
		const request = {};
		const submittedContext = context;
		const submittedData = data;
		const isCurrent = () =>
			mountedRef.current && activeApplyRef.current === request && currentContextRef.current === submittedContext;
		activeApplyRef.current = request;
		setPendingApply({ context: submittedContext, request });
		Promise.resolve()
			.then(() =>
				submittedContext.processDirectiveData ? submittedContext.processDirectiveData(submittedData) : submittedData,
			)
			.then(async (processed) => {
				if (!isCurrent()) {
					return;
				}
				await callback(processed);
				if (!isCurrent()) {
					return;
				}
				// A partially-resolved table applies what matched but must say so:
				// silently dropping rows reads as the apply having failed.
				if (Array.isArray(submittedData) && Array.isArray(processed) && processed.length < submittedData.length) {
					const partialApplyMessage = submittedContext.partialDirectiveApplyMessage;
					if (partialApplyMessage !== null) {
						addToast(
							<Toast severity='warning' title={`Applied ${processed.length} of ${submittedData.length} rows`}>
								{partialApplyMessage ?? (
									<>
										The remaining rows referenced a signal, characteristic, or relative signal that matches nothing on
										this package. Edit the table to fix them, then apply again.
									</>
								)}
							</Toast>,
						);
					}
				}
				setAppliedResult({ context: submittedContext, data: submittedData });
			})
			.catch((err: unknown) => {
				if (!isCurrent()) {
					return;
				}
				const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
				addToast(
					<Toast severity='error' title={`Failed to ${action} data`}>
						{message}
					</Toast>,
				);
			})
			.finally(() => {
				if (!isCurrent()) {
					return;
				}
				activeApplyRef.current = null;
				setPendingApply(null);
			});
	}

	const replaceTooltip =
		disabledReason ??
		(hasAppliedCurrentData ? appliedReason
		: context.replaceHint !== undefined ?
			<div className='flex flex-col gap-2'>
				<p className='mb-1 text-gray-a11'>
					<InfoOutlinedIcon /> {context.replaceHint}
				</p>
			</div>
		:	undefined);

	return (
		<div className='mt-2 flex flex-wrap items-center justify-end gap-2'>
			{hasAppliedCurrentData && <span className='text-xs text-green-11'>Applied</span>}
			{appendContents && (
				<Tooltip enterDelay={250} title={disabledReason ?? (hasAppliedCurrentData ? appliedReason : undefined)}>
					<span>
						<Button
							loading={saving}
							disabled={saving || hasAppliedCurrentData || disabledReason !== undefined}
							onClick={() => applyWith('append', appendContents)}
						>
							<p>Add to data</p>
						</Button>
					</span>
				</Tooltip>
			)}
			{replaceContents && (
				<Tooltip enterDelay={250} title={replaceTooltip}>
					<span>
						<Button
							loading={saving}
							disabled={saving || hasAppliedCurrentData || disabledReason !== undefined}
							onClick={() => applyWith('replace', replaceContents)}
						>
							<p>Replace data</p>
						</Button>
					</span>
				</Tooltip>
			)}
		</div>
	);
}
